import type {
  AddComponentBody,
  ApproveBody,
  ComponentConfig,
  ComponentsResponse,
  ConfigHistoryEntry,
  ConfigSaveBody,
  IncidentActionResult,
  IncidentUpdateBody,
  IncidentsResponse,
  ManualProposalBody,
  OpenIncidentBody,
  Proposal,
  ProposalDetail,
  PublishResult,
  RedecideBody,
  RejectBody,
  ResolveIncidentBody,
  RetireBody,
  RetireResult,
  RetryBody,
  RevertBody,
  RunHistoryPage,
  Scope,
} from '@/domain/types';

import { ApiError, type ConsoleApi, type RunsQuery } from './client';

/**
 * The console API over HTTP, path for path from TDD §11.2, under a configurable base URL.
 * There is no authentication in this phase (§1.3); the network is the only guard.
 * The API lambda does not exist yet, so this implementation is unverified against a live
 * backend: when it lands, check the response shapes against the "console contract" notes in
 * domain/types.ts.
 */
export class HttpConsoleApi implements ConsoleApi {
  private readonly base: string;
  private readonly fetchImpl: typeof fetch;

  constructor(baseUrl: string, fetchImpl: typeof fetch = (input, init) => fetch(input, init)) {
    this.base = baseUrl.replace(/\/+$/, '');
    this.fetchImpl = fetchImpl;
  }

  getComponents(scope: Scope) {
    return this.request<ComponentsResponse>('GET', `/scopes/${enc(scope)}/components`);
  }

  getRuns(scope: Scope, capabilityId: string, opts: RunsQuery = {}) {
    const params = new URLSearchParams();
    params.set('limit', String(opts.limit ?? 50));
    if (opts.before) params.set('before', opts.before);
    return this.request<RunHistoryPage>(
      'GET',
      `/scopes/${enc(scope)}/components/${enc(capabilityId)}/runs?${params}`,
    );
  }

  getQueue(scope: Scope) {
    return this.request<Proposal[]>('GET', `/scopes/${enc(scope)}/proposals`);
  }

  getProposal(proposalId: string) {
    return this.request<ProposalDetail>('GET', `/proposals/${enc(proposalId)}`);
  }

  getIncidents(scope: Scope) {
    return this.request<IncidentsResponse>('GET', `/scopes/${enc(scope)}/incidents`);
  }

  approve(proposalId: string, body: ApproveBody) {
    return this.request<PublishResult>('POST', `/proposals/${enc(proposalId)}/approve`, body);
  }

  reject(proposalId: string, body: RejectBody) {
    return this.request<{ proposal: Proposal }>(
      'POST',
      `/proposals/${enc(proposalId)}/reject`,
      body,
    );
  }

  redecide(proposalId: string, body: RedecideBody) {
    return this.request<PublishResult>('POST', `/proposals/${enc(proposalId)}/redecide`, body);
  }

  retry(proposalId: string, body: RetryBody) {
    return this.request<PublishResult>('POST', `/proposals/${enc(proposalId)}/retry`, body);
  }

  createManualProposal(body: ManualProposalBody) {
    return this.request<PublishResult>('POST', `/proposals`, body);
  }

  openIncident(scope: Scope, body: OpenIncidentBody) {
    return this.request<IncidentActionResult>('POST', `/scopes/${enc(scope)}/incidents`, body);
  }

  postIncidentUpdate(scope: Scope, incidentId: string, body: IncidentUpdateBody) {
    return this.request<IncidentActionResult>(
      'POST',
      `/scopes/${enc(scope)}/incidents/${enc(incidentId)}/updates`,
      body,
    );
  }

  resolveIncident(scope: Scope, incidentId: string, body: ResolveIncidentBody) {
    return this.request<IncidentActionResult>(
      'POST',
      `/scopes/${enc(scope)}/incidents/${enc(incidentId)}/resolve`,
      body,
    );
  }

  getConfig(scope: Scope, capabilityId: string) {
    return this.request<ComponentConfig>('GET', `/config/${enc(scope)}/${enc(capabilityId)}`);
  }

  saveConfig(scope: Scope, capabilityId: string, body: ConfigSaveBody) {
    return this.request<{ config: ComponentConfig }>(
      'PUT',
      `/config/${enc(scope)}/${enc(capabilityId)}`,
      body,
    );
  }

  addComponent(scope: Scope, body: AddComponentBody) {
    return this.request<{ config: ComponentConfig }>('POST', `/config/${enc(scope)}`, body);
  }

  getConfigHistory(scope: Scope, capabilityId: string) {
    return this.request<ConfigHistoryEntry[]>(
      'GET',
      `/config/${enc(scope)}/${enc(capabilityId)}/history`,
    );
  }

  revertConfig(scope: Scope, capabilityId: string, body: RevertBody) {
    return this.request<{ config: ComponentConfig }>(
      'POST',
      `/config/${enc(scope)}/${enc(capabilityId)}/revert`,
      body,
    );
  }

  retireComponent(scope: Scope, capabilityId: string, body: RetireBody) {
    return this.request<RetireResult>(
      'POST',
      `/config/${enc(scope)}/${enc(capabilityId)}/retire`,
      body,
    );
  }

  private async request<T>(
    method: 'GET' | 'POST' | 'PUT',
    path: string,
    body?: unknown,
  ): Promise<T> {
    const init: RequestInit = {
      method,
      headers: { Accept: 'application/json' },
    };
    if (body !== undefined) {
      init.headers = { ...init.headers, 'Content-Type': 'application/json' };
      init.body = JSON.stringify(body);
    }
    const response = await this.fetchImpl(`${this.base}${path}`, init);
    const text = await response.text();
    const parsed: unknown = text ? safeJson(text) : null;
    if (!response.ok) {
      throw new ApiError(response.status, parsed);
    }
    return parsed as T;
  }
}

function enc(segment: string): string {
  return encodeURIComponent(segment);
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { error: 'unparseable_response', detail: text.slice(0, 200) };
  }
}
